import { Corners } from "@glide/common-core/dist/js/components/image-types";
import { getLocalizedString } from "@glide/localization";
import { getAppTabs } from "@glide/common-core/dist/js/components/SerializedApp";
import { generateImageFromSeed } from "@glide/common-core/dist/js/components/triangle-image";
import {
    type ComputationModel,
    type GroundValue,
    type LoadingValue,
    type Row,
    Table,
    isLoadingValue,
    type RootPath,
    Query,
    unwrapLoadingValue,
} from "@glide/computation-model-types";
import { type MinimalAppFacilities } from "@glide/common-core/dist/js/components/types";
import { asMaybeString, asRow } from "@glide/common-core/dist/js/computation-model/data";
import type { Database } from "@glide/common-core/dist/js/Database/core";
import {
    type TableName,
    rowIndexColumnName,
    type TableGlideType,
    BinaryPredicateFormulaOperator,
    SourceColumnKind,
    SpecialValueKind,
    getTableName,
    getTableRefTableName,
    isStringTypeKind,
    isBigTableOrExternal,
} from "@glide/type-schema";
import {
    type ArrayTransform,
    type ClassScreenDescription,
    type EditScreenDescription,
    ArrayTransformKind,
    MutatingScreenKind,
    ScreenDescriptionKind,
    isFormScreen,
    type ConditionalActionNode,
    getScreenProperty,
    getContentTransforms,
} from "@glide/app-description";
import {
    type InputOutputTables,
    doesMutatingScreenAddRows,
    getScreenComponents,
    isClassScreenDescription,
    makeInputOutputTables,
} from "@glide/common-core/dist/js/description";
import { getDocURL } from "@glide/common-core/dist/js/docUrl";
import { makeRowID } from "@glide/common-core/dist/js/make-row-id";
import { Mood } from "@glide/component-utils";
import type { WireButtonComponent } from "@glide/fluent-components/dist/js/fluent-components";
import {
    type ExistingAppDescriptionContext,
    getMutatingKindForScreen,
    isAddClassScreenName,
    isAddOrEditOrFormScreenDescription,
    isEditClassScreenName,
    userProfileScreenName,
} from "@glide/function-utils";
import { type ActionDebugger, inflateCompoundAction } from "@glide/generator/dist/js/actions/compound-handler";
import {
    type ContextTableTypes,
    type HydratedScreenContext,
    type WireActionBackend,
    type WireComponentHydratorWithID,
    type WirePredicate,
    type WireRowComponentHydrationBackend,
    type WireValueGetter,
    type BuilderCallbacks,
    type DynamicFilterResult,
    type HydratedRowContext,
    type SearchableColumns,
    type WireActionHydrator,
    type WireComponentHydrationResult,
    type WireComponentPreHydrationResult,
    type WireRowComponentHydrator,
    type WireRowHydrationValueProvider,
    type WireTableComponentHydrationBackend,
    type WireTableTransformer,
    type WireScreen,
    type WireAction,
    type WireComponent,
    WireActionResultBuilder,
    WireActionResult,
    WireComponentKind,
    makeContextTableTypes,
    HeroImageEffect,
    HeroSize,
    UIBackgroundStyle,
    UIButtonAppearance,
} from "@glide/wire";
import {
    findTabInAppDescription,
    getColumnAssignments,
    makeInputOutputTablesForClassOrArrayScreen,
} from "@glide/generator/dist/js/description-utils";
import { getEditOnSubmitActions } from "@glide/generator/dist/js/form-on-submit";
import { decomposePredicateCombinationFormula } from "@glide/formula-specifications";
import { getUserProfileTableInfo } from "@glide/generator/dist/js/user-profile-info";
import * as utils from "@glide/generator/dist/js/wire/utils";
import { isEmptyOrUndefined, nullToUndefined } from "@glide/support";
import { assert, assertNever, defined, definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import fromPairs from "lodash/fromPairs";
import { type InternalTabHydrator, TableTransformValueProvider } from "./hydration";
import { makeValueGetterForSpecialValue } from "./action-inflation-backend";
import { InflationBackend } from "./inflation";
import {
    addSubscriptionHandler,
    emptyHydratedScreenContext,
    emptySubscriptionInfo,
    emptySubscriptionNeeds,
    makeHydratedScreenContext,
    makeMutableSubscriptionSources,
} from "./internal";
import {
    type ScreenHydrationContext,
    type SubscriptionHandlerAndPath,
    type SubscriptionInfo,
    type SubscriptionSources,
} from "./internal-types";
import { makeSubscriptionHandler } from "./subscription";
import { resolveQueryAsTable } from "./resolve-query";
import { Result } from "@glide/plugins";
import type { WriteSourceType } from "@glide/common-core";

type ScreenContextGetter = (
    hydrationContext: ScreenHydrationContext,
    oldSubscriptionInfo: SubscriptionInfo | undefined
) => [HydratedScreenContext | LoadingValue, SubscriptionInfo | undefined] | undefined;

export type SpecialComponentHydrator = (
    hb: WireRowComponentHydrationBackend,
    isEditValid: boolean,
    haveSearchableComponent: boolean
) => { readonly component: WireComponent; readonly subsidiaryScreen?: WireScreen } | undefined;

interface InflatedScreenCommon {
    readonly tables: InputOutputTables;
    // This will be `undefined` for array screens
    readonly componentHydrators: readonly WireComponentHydratorWithID[] | undefined;
    readonly titleGetter: WireValueGetter;
    readonly filterTransform: WireTableTransformer;
    readonly sortTransform: WireTableTransformer;
    readonly limitTransform: WireTableTransformer;
}

interface InflatedClassScreen {
    readonly tables: InputOutputTables;
    readonly componentHydrators: readonly WireComponentHydratorWithID[];
    readonly specialComponentHydrators: readonly SpecialComponentHydrator[];
    readonly titleGetter: WireValueGetter;

    readonly getScreenContext: ScreenContextGetter;
}

export type DynamicTransformsApplicator = <T extends WireTableComponentHydrationBackend>(
    thb: T,
    table: Table,
    sortTransform: WireTableTransformer,
    limitTransform: WireTableTransformer
) => [thb: T, searchActive: boolean, dynamicFilterResult: DynamicFilterResult | undefined];

interface InflatedUserProfile {
    readonly emailValueGetter: WireValueGetter;
    readonly nameValueGetter: WireValueGetter | undefined;
    readonly imageValueGetter: WireValueGetter | undefined;
}

const dummyUserProfileTable: TableGlideType = {
    name: "$userProfiles",
    columns: [],
    emailOwnersColumn: undefined,
};
const dummyUserProfileTables: ContextTableTypes = makeContextTableTypes(makeInputOutputTables(dummyUserProfileTable));

function inflateFilters(
    ib: InflationBackend | undefined,
    filters: readonly ArrayTransform[],
    inOutputRow: boolean
): WirePredicate {
    if (ib !== undefined) {
        const [predicate] = ib.inflateFilters(filters, inOutputRow);
        return predicate;
    } else {
        for (const filterTransform of filters) {
            if (filterTransform.kind !== ArrayTransformKind.Filter) continue;
            if (filterTransform.isActive === false) continue;
            const condition = decomposePredicateCombinationFormula(filterTransform.predicate)?.spec;
            if (condition === undefined || condition.predicates.length > 0 || condition.combinator !== "and") {
                return () => false;
            }
        }
        return () => true;
    }
}

// If `reference` is `undefined`, then we don't make a reference row.  We do
// that when adding a new/empty row, vs a copy of an existing one.  When
// `reference` is set, then it must be the original row, which we will compare
// against when saving changes.
// https://github.com/quicktype/glide/issues/18406
export type RowToCopyMaker = () => [toCopy: Row, reference: Row | undefined];

export interface ScreenInflatorCallbacks {
    readonly navigateUpAction: WireAction;
    // If sign-in is disabled then you can't sign out, either.
    readonly signOutAction: WireAction | undefined;

    // This has to be callable without a `this`.
    onDataChange(): void;

    fetchTableRows(tableName: TableName): void;

    setColumnsInInvisibleRow(tableName: TableName, row: Row, update: Record<string, GroundValue>): void;
    retireSubscriptionInfo(si: SubscriptionInfo): void;

    getOrAddEmptyRow(
        tableName: TableName,
        key: string,
        makeColumnsToSet: () => Record<string, GroundValue>
    ): Row | undefined;
    getOrCopyRowWithKey(tableName: TableName, makeRow: RowToCopyMaker, key: string): Row | undefined;
    makeRowKeyForScreen(screenName: string, rowID: string | undefined): string;
}

export abstract class ScreenInflator {
    // ##cleanUpInflatedStuff:
    // These all have to be cleaned up when the computation model changes.
    private readonly inflatedClassScreenCommons = new Map<string, InflatedScreenCommon>();
    private readonly inflatedClassScreens = new Map<string, InflatedClassScreen>();
    private inflatedUserProfile: InflatedUserProfile | undefined;
    private inflatedInternalTabs: readonly InternalTabHydrator[] | undefined;
    private readonly inflatedScreenTitles = new Map<
        string,
        readonly [WireValueGetter, InputOutputTables | undefined]
    >();

    constructor(
        public readonly appID: string,
        private readonly appFacilities: MinimalAppFacilities,
        protected readonly context: ExistingAppDescriptionContext,
        public readonly isBuilder: boolean,
        protected readonly computationModel: ComputationModel | undefined,
        private readonly db: Database | undefined,
        protected readonly callbacks: ScreenInflatorCallbacks,
        private readonly precomputedSearchableColumns: SearchableColumns | undefined,
        public readonly writeSource: WriteSourceType
    ) {}

    protected abstract getIsClassScreenSearchNeeded(
        componentHydrators: readonly WireComponentHydratorWithID[]
    ): boolean;
    protected abstract readonly cancelEditSpecialComponent: WireComponent;
    protected abstract makeEditDoneButton(
        action: WireAction | undefined,
        mutatingScreenKind: MutatingScreenKind
    ): WireComponent;
    protected abstract makeAdditionalEditScreenComponents(
        ib: InflationBackend,
        screen: EditScreenDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind
    ): readonly WireComponentHydratorWithID[] | undefined;
    protected abstract makeAdditionalClassScreenSpecialComponents(
        ib: InflationBackend,
        screen: ClassScreenDescription,
        tables: InputOutputTables,
        componentHydrators: readonly WireComponentHydratorWithID[]
    ): readonly SpecialComponentHydrator[] | undefined;
    protected abstract makeDummyUserProfileScreenComponents(
        realEmailAddressGetter: WireValueGetter,
        nameGetter: WireValueGetter
    ): readonly WireComponentHydratorWithID[] | undefined;
    protected abstract makeUserProfileScreenSpecialComponents(
        ib: InflationBackend | undefined,
        screen: ClassScreenDescription | undefined,
        table: TableGlideType
    ): readonly SpecialComponentHydrator[] | undefined;
    protected abstract makeUserProfileScreenAdditionalComponents(
        ib: InflationBackend | undefined,
        screen: ClassScreenDescription | undefined,
        table: TableGlideType
    ): readonly WireComponentHydratorWithID[] | undefined;

    private getRowIndexRootPath(tableName: TableName): RootPath | undefined {
        const maybePaths = defined(this.computationModel).getColumnPaths(tableName)?.get(rowIndexColumnName);
        // This can happen if for whatever reason the comments table is bound
        // to a screen, for example.
        if (maybePaths === undefined) return undefined;
        const [[, rowIndexRootPath]] = maybePaths;
        return rowIndexRootPath;
    }

    protected makeInflationBackend(
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): InflationBackend | undefined {
        if (this.computationModel === undefined) return undefined;

        return new InflationBackend(
            this.appFacilities,
            this.context,
            makeContextTableTypes(tables),
            this.computationModel,
            mutatingScreenKind,
            this.isBuilder,
            this.db,
            this.precomputedSearchableColumns,
            this.writeSource
        );
    }

    public inflateScreenTitle(screenName: string): readonly [WireValueGetter, InputOutputTables | undefined] {
        let tables: InputOutputTables | undefined = undefined;

        function makeUnboundResult(): readonly [WireValueGetter, InputOutputTables | undefined] {
            return [() => null, tables];
        }

        let titleGetter = this.inflatedScreenTitles.get(screenName);
        if (titleGetter !== undefined) {
            return titleGetter;
        }

        let fallbackTitle = screenName === userProfileScreenName ? getLocalizedString("profile") : undefined;

        if (screenName === userProfileScreenName) {
            const tableName = getUserProfileTableInfo(this.context.appDescription)?.tableName;
            const table = this.context.findTable(tableName);
            return [() => fallbackTitle, definedMap(table, makeInputOutputTables)];
        }

        const screen = this.context.appDescription.screenDescriptions[screenName];
        if (!isClassScreenDescription(screen)) return makeUnboundResult();

        const tab = findTabInAppDescription(screenName, this.context.appDescription);
        if (tab !== undefined) {
            const tabTitle = tab[0].title;
            if (!isEmptyOrUndefined(tabTitle)) {
                fallbackTitle = tabTitle;
            }
        }

        tables = makeInputOutputTablesForClassOrArrayScreen(screen, t => this.context.findTable(t));
        if (tables === undefined) return makeUnboundResult();

        // Array screens don't need a title getter
        if (screen.kind !== ScreenDescriptionKind.Class) return makeUnboundResult();

        const mutatingScreenKind = getMutatingKindForScreen(screenName, screen);
        const ib = this.makeInflationBackend(tables, mutatingScreenKind);
        if (ib === undefined) return makeUnboundResult();

        const titleValueGetter = ib.getValueGetterForProperty(screen.title, true)[0];
        titleGetter = [
            hb => {
                const v = titleValueGetter(hb);
                if (utils.isNotEmptyDisplayValue(v)) {
                    return v;
                } else {
                    return fallbackTitle;
                }
            },
            tables,
        ];
        this.inflatedScreenTitles.set(screenName, titleGetter);
        return titleGetter;
    }

    // This caches its results, which means it's ok to call it any number of
    // times for the same screen and get the same result.
    public inflateScreenCommon(screenName: string, screen: ClassScreenDescription): InflatedScreenCommon | undefined {
        let common = this.inflatedClassScreenCommons.get(screenName);
        if (common !== undefined) {
            return common;
        }

        const [titleGetter, tables] = this.inflateScreenTitle(screenName);
        if (tables === undefined) return undefined;

        this.callbacks.fetchTableRows(getTableName(tables.input));

        const mutatingScreenKind = getMutatingKindForScreen(screenName, screen);
        const ib = this.makeInflationBackend(tables, mutatingScreenKind);
        if (ib === undefined) return undefined;

        const componentHydrators =
            screen.kind === ScreenDescriptionKind.Class
                ? getScreenComponents(screen).map(desc =>
                      utils.inflateComponent(ib, desc, mutatingScreenKind !== undefined)
                  )
                : undefined;

        const {
            filter: filterTransform,
            sort: sortTransform,
            limit: limitTransform,
        } = ib.inflateTransforms(getContentTransforms(screen));

        common = { tables, componentHydrators, titleGetter, filterTransform, sortTransform, limitTransform };
        this.inflatedClassScreenCommons.set(screenName, common);
        return common;
    }

    private subscribeToScreenContext(
        screenName: string,
        screen: ClassScreenDescription,
        hydrationContext: ScreenHydrationContext,
        sources: SubscriptionSources
    ): SubscriptionInfo {
        const handler = makeSubscriptionHandler(sources, this.callbacks.onDataChange);
        let handlerAndPath: SubscriptionHandlerAndPath | undefined;
        if (handler !== undefined) {
            const subscriptionPath = addSubscriptionHandler(
                hydrationContext,
                `${screen.kind}-screen-context ${screenName}`,
                handler
            );
            handlerAndPath = {
                handler,
                path: subscriptionPath,
            };
        }
        const subscriptionInfo: SubscriptionInfo = {
            isComponentState: false,
            handlerAndPath,
            tokens: new Set(),
            needs: sources.needs,
            subsidiaries: [],
        };
        return subscriptionInfo;
    }

    // Maybe this doesn't belong in here?
    public fetchScreenData(
        screenName: string,
        screen: ClassScreenDescription,
        hydrationContext: ScreenHydrationContext,
        applySortAndLimit: boolean
    ):
        | [
              context: HydratedRowContext | LoadingValue,
              si: SubscriptionInfo,
              table: Table,
              sortTransform: WireTableTransformer,
              limitTransform: WireTableTransformer
          ]
        | undefined {
        const { computationModel } = this;
        if (computationModel === undefined) return undefined;

        const common = this.inflateScreenCommon(screenName, screen);
        if (common === undefined) return undefined;
        const { tables, filterTransform, sortTransform, limitTransform } = common;

        const tableName = getTableName(tables.input);

        const sources = makeMutableSubscriptionSources();
        const rowIndexRootPath = this.getRowIndexRootPath(tableName);
        if (rowIndexRootPath === undefined) return undefined;

        // TODO: We don't need to subscribe to the row index if
        // the transformer subscribes to any column.
        sources.columnsInAllDirectRows.get(rowIndexRootPath.rest.key).add(rowIndexColumnName);

        const ttvp = new TableTransformValueProvider(hydrationContext, tableName, sources, undefined, true, []);

        // We have to subscribe after adding sources, not here.
        const subscribe = () => this.subscribeToScreenContext(screenName, screen, hydrationContext, sources);

        let tableData: Table | LoadingValue | undefined;
        if (isBigTableOrExternal(tables.input)) {
            // FIXME: this has to be memoized
            const ib = this.makeInflationBackend(tables, getMutatingKindForScreen(screenName, screen));
            if (ib === undefined) return undefined;

            const queryTransformer = ib.inflateQueryTransformer(tables.input, getContentTransforms(screen));

            const hb = ttvp.makeRowValueProvider(undefined);
            const query = queryTransformer(hb, new Query(tableName));

            if (query === undefined || isLoadingValue(query)) {
                tableData = query;
            } else {
                // Screen contexts are a single row
                tableData = resolveQueryAsTable(query.withLimit(1), hydrationContext, sources, unwrapLoadingValue);
            }
        } else {
            hydrationContext.fetchTableData(tableName);
            tableData = computationModel.getBaseDataForTable(tableName);
        }

        if (isLoadingValue(tableData)) {
            return [tableData, subscribe(), new Table(), sortTransform, limitTransform];
        }

        let rows: Table | undefined;
        if (isBigTableOrExternal(tables.input)) {
            rows = tableData;
        } else {
            rows = definedMap(tableData, t => filterTransform(ttvp, t));
            if (applySortAndLimit) {
                rows = definedMap(rows, t => limitTransform(ttvp, sortTransform(ttvp, t)));
            }
        }
        rows = rows ?? new Table();

        let screenContext: HydratedRowContext;
        if (rows.size === 0) {
            screenContext = emptyHydratedScreenContext;
        } else {
            screenContext = makeHydratedScreenContext(rows.asArray(), undefined, undefined);
        }

        return [screenContext, subscribe(), rows, sortTransform, limitTransform];
    }

    protected fetchSpecificInputRows(
        screenName: string,
        screen: ClassScreenDescription,
        hydrationContext: ScreenHydrationContext,
        withFilter: boolean
    ):
        | {
              readonly table: Table | LoadingValue;
              readonly subscriptionInfo: SubscriptionInfo;
              readonly sortTransform: WireTableTransformer;
              readonly limitTransform: WireTableTransformer;
          }
        | undefined {
        if (hydrationContext.internalScreen === undefined) return undefined;
        const { inputRowIDs } = hydrationContext.internalScreen.context;

        const common = this.inflateScreenCommon(screenName, screen);
        if (common === undefined) return undefined;
        const { tables, filterTransform, sortTransform, limitTransform } = common;

        const tableName = getTableName(tables.input);
        const isQueryable = isBigTableOrExternal(tables.input);

        const sources = makeMutableSubscriptionSources();
        const rowIndexRootPath = this.getRowIndexRootPath(tableName);
        if (rowIndexRootPath === undefined) return undefined;

        // First we try to get the input rows from the table data we have.  If
        // there are rows we can't find, and this is a queryable data source,
        // we make a query.
        hydrationContext.fetchTableData(tableName);
        let tableData = this.computationModel?.getBaseDataForTable(tableName);

        let tableDataFromQuery: Table | LoadingValue | undefined;
        if (isQueryable) {
            const rowIDColumnName = tables.input.rowIDColumn;
            if (inputRowIDs.length > 0 && rowIDColumnName !== undefined) {
                // We're only fetching the first row ID.  Apps support
                // multiple, in array screens.
                const query = new Query(tableName).withCondition({
                    kind: BinaryPredicateFormulaOperator.Equals,
                    lhs: { columnName: rowIDColumnName },
                    rhs: inputRowIDs[0],
                    negated: false,
                });
                // We do this query whether we get the row from the
                // computation model or not, because the query might be stale,
                // i.e. we might have done a "Reload Query" action since we
                // last got this row.  In that case this will re-query from
                // the data source and update the row in the computation
                // model.
                tableDataFromQuery = resolveQueryAsTable(query, hydrationContext, sources, unwrapLoadingValue);
            }
        }

        let rows: Row[] = [];

        for (const fromQuery of [false, true]) {
            if (fromQuery) {
                if (tableDataFromQuery === undefined) break;
                assert(isQueryable);
                tableData = tableDataFromQuery;
            }

            rows = [];
            for (const rowID of inputRowIDs) {
                sources.columnsInRows.get(rowIndexRootPath.rest.key).get(rowID).add(rowIndexColumnName);

                if (isLoadingValue(tableData)) continue;

                const row = tableData?.get(rowID);
                if (row === undefined) continue;
                rows.push(row);
            }

            if (rows.length === inputRowIDs.length) break;
        }

        let resultTable: Table | LoadingValue;
        if (isLoadingValue(tableData)) {
            resultTable = tableData;
        } else {
            resultTable = new Table(rows);
            if (withFilter) {
                const ttvp = new TableTransformValueProvider(hydrationContext, tableName, sources, undefined, true, []);
                resultTable = filterTransform(ttvp, resultTable);
            }
        }

        const subscriptionInfo = this.subscribeToScreenContext(screenName, screen, hydrationContext, sources);

        return { table: resultTable, subscriptionInfo, sortTransform, limitTransform };
    }

    private inflateRegularClassScreen(
        screenName: string,
        screen: ClassScreenDescription
    ): InflatedClassScreen | undefined {
        const common = this.inflateScreenCommon(screenName, screen);
        if (common === undefined) return undefined;
        const { tables, titleGetter } = common;
        let { componentHydrators } = common;
        assert(componentHydrators !== undefined);

        const mutatingScreenKind = getMutatingKindForScreen(screenName, screen);
        const ib = this.makeInflationBackend(tables, mutatingScreenKind);
        if (ib === undefined) return undefined;

        const specialComponentHydrators: SpecialComponentHydrator[] = [];

        const outputTableName = getTableName(tables.output);

        let getOutputValues: ((hb: WireRowHydrationValueProvider) => Record<string, GroundValue>) | undefined;

        if (mutatingScreenKind !== undefined) {
            assert(isAddOrEditOrFormScreenDescription(screenName, screen));

            const outputValueGetters = utils.inflateColumnAssignments(
                ib,
                tables.output,
                getColumnAssignments(screen),
                doesMutatingScreenAddRows(mutatingScreenKind)
            );

            getOutputValues = (hb: WireRowHydrationValueProvider) => {
                return fromPairs(
                    mapFilterUndefined(outputValueGetters, ([n, g]) => {
                        const v = g(hb);
                        // We ignore unbound values
                        if (v === null) return undefined;
                        return [n, v];
                    })
                );
            };

            const onSubmitHydrator = utils.inflateActions(
                ib.makeInflationBackendForOutputTable(),
                getEditOnSubmitActions(screen)
            );

            specialComponentHydrators.push(
                (hb, isEditValid) => {
                    let action: WireAction | undefined;

                    if (
                        isEditValid &&
                        utils.getCanEditFromNetworkStatus(
                            hb,
                            this.context.eminenceFlags,
                            mutatingScreenKind === MutatingScreenKind.EditScreen
                        )
                    ) {
                        const { rowContext: rowScreenContext } = hb;
                        assert(rowScreenContext !== undefined);

                        const { outputRow } = rowScreenContext;
                        // This can happen when the
                        // ##outputTableIsntLoadedYet.  Once it's loaded we'll
                        // rehydrate and everything will be fine.
                        if (outputRow === undefined) return undefined;

                        const onSubmit = utils.hydrateOnSubmitAction(onSubmitHydrator, () =>
                            hb.makeHydrationBackendForOutputRow()
                        );

                        if (onSubmit !== false) {
                            const columnAssignmentValues = defined(getOutputValues)(hb);

                            let saveChanges: (
                                ab: WireActionBackend
                            ) => Promise<[result: Result, newRow: Row | undefined]>;
                            // The "row that's being edited" becomes the
                            // input row in the on-submit action. In Edit
                            // screens, however, the output row is a copy
                            // of the edited row, and has a different row
                            // ID, which we don't want to expose, so we
                            // use the input row, which is the original
                            // row that we've just written back to. In
                            // Form screens that is not a problem, but we
                            // do need to use the new row there, because
                            // the invisible row that's our current output
                            // row has already been removed from the
                            // table.
                            // https://github.com/quicktype/glide/issues/12551
                            if (mutatingScreenKind === MutatingScreenKind.EditScreen) {
                                saveChanges = async ab => [
                                    await ab.saveEditScreenChanges(outputTableName, rowScreenContext),
                                    undefined,
                                ];
                            } else {
                                saveChanges = async ab => {
                                    const addResult = await ab.addRow(outputTableName, outputRow, undefined, true);
                                    if (addResult.ok) {
                                        return [Result.Ok(), addResult.result];
                                    } else {
                                        return [addResult, undefined];
                                    }
                                };
                            }

                            action = utils.registerBusyActionRunner(hb, "onSubmitButton", () => [
                                async (ab, handled) => {
                                    ab.navigateUp();
                                    if (columnAssignmentValues !== undefined) {
                                        this.callbacks.setColumnsInInvisibleRow(
                                            outputTableName,
                                            outputRow,
                                            columnAssignmentValues
                                        );
                                    }
                                    const [result, newRow] = await saveChanges(ab);
                                    if (!result.ok) return WireActionResult.fromResult(result);
                                    // The ##onSubmitActionContext is the
                                    // output row.
                                    if (onSubmit !== undefined) {
                                        const onSubmitAB = ab.makeActionBackendForOnSubmit(newRow);
                                        return await onSubmitAB.invoke(
                                            "submit add/edit/form screen",
                                            onSubmit,
                                            handled
                                        );
                                    }
                                    return WireActionResult.nondescriptSuccess();
                                },
                                undefined,
                            ]);
                        }
                    }

                    return { component: this.makeEditDoneButton(action, mutatingScreenKind) };
                },
                () => ({ component: this.cancelEditSpecialComponent })
            );
        } else {
            specialComponentHydrators.push(
                ...(this.makeAdditionalClassScreenSpecialComponents(ib, screen, tables, componentHydrators) ?? [])
            );
        }

        const isSearchActive = this.getIsClassScreenSearchNeeded(componentHydrators);
        if (isSearchActive) {
            // Modify component hydrators to not show non-searched components
            // if search is active.
            componentHydrators = componentHydrators.map(hydratorWithID => {
                const [hydrator, id] = hydratorWithID;
                // Components that want search handle it themselves, so we
                // just return their original hydrator.  All other components
                // need to disappear when search becomes active.
                if (hydrator.wantsSearch) {
                    return hydratorWithID;
                }

                class HydratorWrapper implements WireRowComponentHydrator {
                    public static readonly wantsSearch = true;

                    // If the search is active then we don't set this.
                    private readonly hydrator: WireRowComponentHydrator | undefined;

                    constructor(hb: WireRowComponentHydrationBackend, builder: BuilderCallbacks | undefined) {
                        const [, needle] = utils.getScreenSearchState(hb);
                        if (needle !== "") return;

                        this.hydrator = new hydrator(hb, builder);
                    }

                    public preHydrate(): WireComponentPreHydrationResult {
                        return this.hydrator?.preHydrate?.() ?? [true, undefined];
                    }

                    public hydrate(): WireComponentHydrationResult | undefined {
                        return this.hydrator?.hydrate();
                    }
                }

                return [HydratorWrapper, id];
            });
        }

        if (mutatingScreenKind === MutatingScreenKind.EditScreen) {
            assert(isAddOrEditOrFormScreenDescription(screenName, screen));

            componentHydrators = [
                ...componentHydrators,
                ...(this.makeAdditionalEditScreenComponents(ib, screen, tables, mutatingScreenKind) ?? []),
            ];
        }

        return {
            tables,
            componentHydrators,
            specialComponentHydrators,
            titleGetter,
            getScreenContext: (hydrationContext, oldSubscriptionInfo) => {
                // TODO: Don't get new context if subscription is not dirty.

                // TODO: I'm not sure this subscription stuff should be
                // in here, or cloistered off somewhere else.
                if (oldSubscriptionInfo !== undefined) {
                    this.callbacks.retireSubscriptionInfo(oldSubscriptionInfo);
                }

                if (screen.fetchesData === true) {
                    // TODO: Don't make new subscription if we have one.

                    // We've already done all the things that could fail in
                    // this function, so we're asserting that it doesn't.
                    const maybeScreenData = this.fetchScreenData(screenName, screen, hydrationContext, true);
                    // We need to return a screen context, vs `undefined`
                    // here, because some apps have incorrectly configured
                    // tabs (they're bound to the "comments" table, for
                    // example), but still expect the tab to act "regularly",
                    // because OCM did that.
                    if (maybeScreenData === undefined) return [emptyHydratedScreenContext, emptySubscriptionInfo];
                    const [hydratedScreenContext, subscriptionInfo] = maybeScreenData;
                    return [hydratedScreenContext, subscriptionInfo];
                } else {
                    const result = this.fetchSpecificInputRows(screenName, screen, hydrationContext, false);
                    if (result === undefined) return undefined;
                    const { table, subscriptionInfo } = result;

                    if (isLoadingValue(table)) {
                        return [table, subscriptionInfo];
                    }

                    const rows = table.asArray();

                    // FIXME: simplify
                    function maybeGetOutputValues() {
                        return {};
                    }

                    let outputRow: Row | undefined;
                    if (isAddClassScreenName(screenName)) {
                        const tn = getTableRefTableName(screen.type);
                        outputRow = this.callbacks.getOrAddEmptyRow(tn, screenName, maybeGetOutputValues);
                    } else {
                        const row = rows[0];
                        const key = this.callbacks.makeRowKeyForScreen(screenName, row?.$rowID);
                        if (isFormScreen(screen)) {
                            const tn = getTableRefTableName(screen.formType);
                            outputRow = this.callbacks.getOrAddEmptyRow(tn, key, maybeGetOutputValues);
                        } else if (isEditClassScreenName(screenName) && row !== undefined) {
                            const tn = getTableRefTableName(screen.type);
                            outputRow = this.callbacks.getOrCopyRowWithKey(
                                tn,
                                () => [
                                    {
                                        ...row,
                                        ...maybeGetOutputValues(),
                                        $rowID: makeRowID(),
                                        $isVisible: false,
                                    },
                                    row,
                                ],
                                key
                            );
                        }
                    }

                    return [makeHydratedScreenContext(rows, outputRow, undefined), subscriptionInfo];
                }
            },
        };
    }

    private inflateUserProfileScreen(screen: ClassScreenDescription | undefined): InflatedClassScreen | undefined {
        let tables: InputOutputTables;
        let componentHydrators: readonly WireComponentHydratorWithID[];
        let getScreenContext: ScreenContextGetter;
        let titleGetter: WireValueGetter;

        if (this.computationModel === undefined) return undefined;

        const info = getUserProfileTableInfo(this.context.appDescription);

        let ib: InflationBackend | undefined;

        // The user profile screen won't work if the user profile `info` isn't
        // set up correctly, so we just make the dummy one in that case.
        if (screen !== undefined && info !== undefined) {
            // We ignore the table transformer here because the user profile
            // screen isn't supposed to have transforms.
            const common = this.inflateScreenCommon(userProfileScreenName, screen);
            if (common === undefined) return undefined;

            tables = common.tables;
            componentHydrators = defined(common.componentHydrators);
            titleGetter = common.titleGetter;

            getScreenContext = (hydrationContext, oldSubscriptionInfo) => {
                if (oldSubscriptionInfo !== undefined) {
                    this.callbacks.retireSubscriptionInfo(oldSubscriptionInfo);
                }

                let row: Row | undefined;
                let subscriptionInfo: SubscriptionInfo | undefined;
                const { computationModel } = this;
                if (computationModel !== undefined) {
                    const path = computationModel.getUserProfileRowPath();
                    if (path !== undefined) {
                        const handler = defined(
                            makeSubscriptionHandler(
                                {
                                    globalKeys: new Map([[path.rest.key, true]]),
                                    columnsInRows: new Map(),
                                    columnsInAllDirectRows: new Map(),
                                    columnsInAllIndirectRows: new Map(),
                                    needs: emptySubscriptionNeeds,
                                },
                                this.callbacks.onDataChange
                            )
                        );
                        const subscriptionPath = addSubscriptionHandler(
                            hydrationContext,
                            "user-profile-screen-context",
                            handler
                        );
                        subscriptionInfo = {
                            isComponentState: false,
                            handlerAndPath: { handler, path: subscriptionPath },
                            tokens: new Set(),
                            needs: emptySubscriptionNeeds,
                            subsidiaries: [],
                        };
                        const rowValue = computationModel.ns.get(path);
                        if (!isLoadingValue(rowValue)) {
                            row = definedMap(rowValue, asRow);
                        }
                    }
                }
                return [makeHydratedScreenContext(row, undefined, undefined), subscriptionInfo];
            };

            ib = this.makeInflationBackend(tables, undefined);
        } else {
            tables = dummyUserProfileTables;

            const [realEmailAddressGetter] = makeValueGetterForSpecialValue(
                SpecialValueKind.RealEmailAddress,
                this.computationModel
            );
            const [userNameGetter] = makeValueGetterForSpecialValue(SpecialValueKind.UserName, this.computationModel);

            componentHydrators =
                this.makeDummyUserProfileScreenComponents(realEmailAddressGetter, userNameGetter) ?? [];

            getScreenContext = () => [emptyHydratedScreenContext, undefined];

            titleGetter = () => getLocalizedString("profile");
        }

        const specialComponentHydrators = this.makeUserProfileScreenSpecialComponents(ib, screen, tables.input) ?? [];
        const additionalComponents = this.makeUserProfileScreenAdditionalComponents(ib, screen, tables.input);
        if (additionalComponents !== undefined) {
            componentHydrators = componentHydrators.concat(additionalComponents);
        }

        return {
            tables,
            getScreenContext,
            titleGetter,
            componentHydrators,
            specialComponentHydrators,
        };
    }

    public inflateClassScreen(
        screenName: string,
        screen: ClassScreenDescription | undefined
    ): InflatedClassScreen | undefined {
        let inflatedScreen = this.inflatedClassScreens.get(screenName);
        if (inflatedScreen === undefined) {
            if (screenName === userProfileScreenName) {
                inflatedScreen = this.inflateUserProfileScreen(screen);
            } else {
                inflatedScreen = this.inflateRegularClassScreen(screenName, defined(screen));
            }
            if (inflatedScreen === undefined) return undefined;
            this.inflatedClassScreens.set(screenName, inflatedScreen);
        }
        return inflatedScreen;
    }

    public inflateUserProfile(): InflatedUserProfile | undefined {
        if (this.inflatedUserProfile !== undefined) return this.inflatedUserProfile;

        if (this.computationModel === undefined) return undefined;

        const ib = this.makeInflationBackend(dummyUserProfileTables, undefined);
        if (ib === undefined) return undefined;

        const info = getUserProfileTableInfo(this.context.appDescription);

        const [emailGetter] = makeValueGetterForSpecialValue(SpecialValueKind.RealEmailAddress, this.computationModel);
        const [nameGetter] = makeValueGetterForSpecialValue(SpecialValueKind.UserName, this.computationModel);

        let inflated: InflatedUserProfile = {
            emailValueGetter: emailGetter,
            nameValueGetter: nameGetter,
            imageValueGetter: undefined,
        };

        if (info !== undefined) {
            const [imageGetter, imageType] = ib.getValueGetterForSourceColumn(
                { kind: SourceColumnKind.UserProfile, name: [info.imageColumnName] },
                false,
                false
            );
            if (imageType !== undefined && isStringTypeKind(imageType.kind)) {
                inflated = { ...inflated, imageValueGetter: imageGetter };
            }
        }

        this.inflatedUserProfile = inflated;
        return inflated;
    }

    public inflateInternalTabs(): readonly InternalTabHydrator[] {
        if (this.inflatedInternalTabs !== undefined) {
            return this.inflatedInternalTabs;
        }

        const appTabs = getAppTabs(this.context.appDescription);

        const userProfileTableName = this.context.userProfileTableInfo?.tableName;
        const userProfileTable = definedMap(userProfileTableName, tn => this.context.findTable(tn));

        let ib: InflationBackend | undefined;
        if (userProfileTable !== undefined) {
            ib = this.makeInflationBackend(makeInputOutputTables(userProfileTable), undefined);
        }

        const inflatedInternalTabs: InternalTabHydrator[] = appTabs.map(tab => {
            const tabScreenName = getScreenProperty(tab.screenName);
            let predicate: WirePredicate;
            if (tab.hidden === true) {
                predicate = () => false;
            } else {
                predicate = inflateFilters(ib, tab.visibilityFilters ?? [], false);
            }

            return hb => {
                if (userProfileTableName !== undefined) {
                    // If we don't do this, an app that has conditions on all
                    // tabs might never fetch any data.
                    hb.context.fetchTableData(userProfileTableName);
                }

                let isVisible = predicate(hb);
                if (isLoadingValue(isVisible)) {
                    isVisible = false;
                }
                return {
                    isVisible,
                    visibilityPredicate: predicate,
                    visibilitySubscriptionInfo: hb.subscribe(
                        `tab-visibility ${tabScreenName}`,
                        this.callbacks.onDataChange
                    ),
                    tabScreenName: tabScreenName ?? "Missing tab screen name",
                };
            };
        });
        // If we don't have an inflation backend it means that we'll have to
        // re-inflate the tabs when we have one, so we don't save the inflated
        // tabs.
        if (ib !== undefined) {
            this.inflatedInternalTabs = inflatedInternalTabs;
        }
        return inflatedInternalTabs;
    }

    public inflateCompoundAction(
        action: ConditionalActionNode,
        tables: InputOutputTables,
        actionDebugger: ActionDebugger
    ): WireActionHydrator | WireActionResult {
        const ib = this.makeInflationBackend(tables, undefined);
        if (ib === undefined) return WireActionResult.nondescriptError(false, "Not initialized");

        return inflateCompoundAction(ib, action, WireActionResultBuilder.nondescript(), undefined, actionDebugger);
    }
}

class PageScreenInflator extends ScreenInflator {
    protected get cancelEditSpecialComponent(): WireButtonComponent {
        return {
            kind: WireComponentKind.Button,
            title: getLocalizedString("cancel"),
            appearance: UIButtonAppearance.Bordered,
            action: this.callbacks.navigateUpAction,
        };
    }

    protected getIsClassScreenSearchNeeded(): boolean {
        // There is no screen search in Pages.
        return false;
    }

    protected makeEditDoneButton(
        action: WireAction | undefined,
        mutatingScreenKind: MutatingScreenKind
    ): WireComponent {
        if (mutatingScreenKind === MutatingScreenKind.FormScreen) {
            const button: WireButtonComponent = {
                kind: WireComponentKind.Button,
                title: getLocalizedString("submit"),
                appearance: UIButtonAppearance.Filled,
                action,
            };
            return button;
        } else if (mutatingScreenKind === MutatingScreenKind.EditScreen) {
            const button: WireButtonComponent = {
                kind: WireComponentKind.Button,
                title: getLocalizedString("submit"),
                appearance: UIButtonAppearance.Filled,
                action,
            };
            return button;
        } else {
            return assertNever(mutatingScreenKind);
        }
    }

    protected makeAdditionalEditScreenComponents(): readonly WireComponentHydratorWithID[] | undefined {
        return undefined;
    }

    protected makeAdditionalClassScreenSpecialComponents(): readonly SpecialComponentHydrator[] | undefined {
        return undefined;
    }

    protected makeDummyUserProfileScreenComponents(
        realEmailAddressGetter: WireValueGetter
    ): readonly WireComponentHydratorWithID[] | undefined {
        const heroHydrator: WireComponentHydratorWithID = [
            utils.makeSimpleWireRowComponentHydratorConstructor(hb => {
                const emailAddressValue = nullToUndefined(realEmailAddressGetter(hb));
                const emailAddress = isLoadingValue(emailAddressValue) ? undefined : asMaybeString(emailAddressValue);
                const image = generateImageFromSeed(emailAddress ?? "", false);
                return {
                    component: {
                        kind: WireComponentKind.Hero,
                        subtitle: emailAddress,
                        image,
                        backgroundEffect: HeroImageEffect.None,
                        withBlur: false,
                        bgStyle: UIBackgroundStyle.Card,
                        imageCorners: Corners.Rounded,
                        size: HeroSize.Medium,
                        buttons: [],
                    },
                    isValid: true,
                };
            }),
            makeRowID(),
        ];
        const hintHydrator: WireComponentHydratorWithID = [
            utils.makeSimpleWireRowComponentHydratorConstructor(hb => {
                const token = hb.registerAction("profile", async ab => {
                    ab.actionCallbacks.openLink(getDocURL("userProfile"));
                    return WireActionResult.nondescriptSuccess();
                });
                return {
                    component: {
                        kind: WireComponentKind.Hint,
                        description: "You haven't set up the User Profile table yet.",
                        mood: Mood.Neutral,
                        actionTitle: "Learn more",
                        action: { token },
                    },
                    isValid: true,
                };
            }),
            makeRowID(),
        ];

        return [hintHydrator, heroHydrator];
    }

    protected makeUserProfileScreenSpecialComponents(): readonly SpecialComponentHydrator[] | undefined {
        return undefined;
    }

    protected makeUserProfileScreenAdditionalComponents(): readonly WireComponentHydratorWithID[] | undefined {
        return undefined;
    }
}

export function makeScreenInflator(...args: ConstructorParameters<typeof ScreenInflator>): ScreenInflator {
    return new PageScreenInflator(...args);
}
